import _ from "lodash";
import React, { Fragment, useCallback, useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useParams } from "react-router-dom";
import { shallow } from "zustand/shallow";
import Breadcrumb from "../../components/Breadcrumb";
import AudioRecordButton from "../../components/form/AudioRecordButton";
import ImageThumbnailGallery from "../../components/ImageThumbnailGallery";
import PrevNextNav, { usePrevNextNav } from "../../components/PrevNextNav";
import Spinner from "../../components/misc/Spinner";
import ConfirmDialog from "../../components/modal/ConfirmDialog";
import CountdownDialog from "../../components/modal/CountdownDialog";
import { SettingsContext } from "../../config/settings";
import { DEV_MODE } from "../../constants";
import * as deliveryNotesApi from "../../services/delivery_notes";
import * as usersApi from "../../services/users";
import { Role, useAuthStore } from "../../stores/auth";
import { DELIVERY_NOTE_ATTRS, DELIVERY_NOTE_STATE, useDeliveryNotesStore } from "../../stores/delivery_notes";
import { useToastStore } from "../../stores/toast";
import { formatDate, formatDateTime } from "../../utils/dateFormat";
import DeliveryNoteDetailsDocTable from "./DeliveryNoteDetailsDocTable";
import MobileActionBarContent from "../../components/mobile-action-bar/MobileActionBarContent";
import DeliveryNoteDetailsNoteTable from "./DeliveryNoteDetailsNoteTable";
import DeliveryNoteEmailModal from "./DeliveryNoteEmailModal";

export default function DeliveryNoteDetails() {
  const { t } = useTranslation();
  const params = useParams();
  const noteId = Number.parseInt(params.noteId);
  const { whitelabel } = useContext(SettingsContext);

  const [me, hasRole] = useAuthStore((state) => [state.me, state.hasRole], shallow);
  const [
    byIds,
    allIds,
    total,
    lastFetchParams,
    getById,
    fetchSome,
    changeState,
    generatePdf,
    isInState,
    isOwner,
    addComment,
    updateComment,
    deleteComment,
    assignDeliveryNote,
    removeFromList,
  ] = useDeliveryNotesStore(
    (state) => [
      state.byIds,
      state.allIds,
      state.total,
      state.lastFetchParams,
      state.getById,
      state.fetchSome,
      state.changeState,
      state.generatePdf,
      state.isInState,
      state.isOwner,
      state.addComment,
      state.updateComment,
      state.deleteComment,
      state.assignDeliveryNote,
      state.removeFromList,
    ],
    shallow,
  );

  const isLoading = !allIds.includes(noteId);
  const deliveryNote = byIds[noteId];

  const buildNoteUrl = useCallback((id) => `/delivery_notes/${id}/details`, []);

  const {
    prevId: prevNavId,
    nextId: nextNavId,
    hasPrevPage,
    hasNextPage,
    navigatePrev,
    navigateNext,
    visible: navVisible,
  } = usePrevNextNav({
    currentId: noteId,
    allIds,
    total,
    lastFetchParams,
    fetchPage: fetchSome,
    buildUrl: buildNoteUrl,
  });

  const isCreator = isOwner(deliveryNote, me);
  const isInDraftedState = isInState(deliveryNote, DELIVERY_NOTE_STATE.DRAFTED);
  const isManager = hasRole(Role.ADMINISTRATOR) || hasRole(Role.MANAGER) || hasRole(Role.OFFICE);
  const canReview = !isInDraftedState && isManager;
  const canEdit =
    hasRole(Role.ADMINISTRATOR) || ((hasRole(Role.MANAGER) || hasRole(Role.OFFICE)) && !isInDraftedState) || (isCreator && isInDraftedState);

  const [invoiceNumber, setInvoiceNumber] = useState("");
  const [generatingPdf, setGeneratingPdf] = useState(false);
  const [showConfirmReview, setShowConfirmReview] = useState(false);
  const [showConfirmBilled, setShowConfirmBilled] = useState(false);
  const [showConfirmCanceled, setShowConfirmCanceled] = useState(false);
  const [commentText, setCommentText] = useState("");
  const [editingCommentId, setEditingCommentId] = useState(null);
  const [editingCommentText, setEditingCommentText] = useState("");
  const [showConfirmDelete, setShowConfirmDelete] = useState(null);
  const [submittingComment, setSubmittingComment] = useState(false);
  const [allUsers, setAllUsers] = useState([]);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [showConfirmCopy, setShowConfirmCopy] = useState(false);
  const [pendingAdvanceUrl, setPendingAdvanceUrl] = useState(null);
  const navigate = useNavigate();
  const addToast = useToastStore((state) => state.addToast);

  useEffect(() => {
    getById({ id: noteId });
    setPendingAdvanceUrl(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [noteId]);

  useEffect(() => {
    if (isManager) {
      usersApi.getAll().then(setAllUsers);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setInvoiceNumber(deliveryNote?.invoice_number || "");
  }, [deliveryNote, noteId]);

  const listUrl = lastFetchParams
    ? `/delivery_notes?${new URLSearchParams({
        page: Math.floor(lastFetchParams.offset / lastFetchParams.limit),
        sort_by: lastFetchParams.sort_by,
        sort_dir: lastFetchParams.sort_dir,
        ...(lastFetchParams.filter && { filter: lastFetchParams.filter }),
        ...(lastFetchParams.q && { q: lastFetchParams.q }),
      })}`
    : "/delivery_notes";

  if (isLoading) {
    return (
      <div>
        <Breadcrumb
          items={[
            { label: t("delivery_notes.delivery_notes"), to: listUrl },
            { label: `${t("common.number_short")} …` },
          ]}
        >
          <PrevNextNav
            currentId={noteId}
            allIds={allIds}
            total={total}
            lastFetchParams={lastFetchParams}
            fetchPage={fetchSome}
            buildUrl={buildNoteUrl}
          />
        </Breadcrumb>
        <Spinner />
      </div>
    );
  }

  const customer = deliveryNote.customer;
  const hasCustomer =
    customer?.number ||
    customer?.name ||
    customer?.addresses?.some((a) => a.street || a.zip || a.city || a.district || a.state || a.country);
  const property = deliveryNote.customer_property;
  const hasProperty =
    property?.name ||
    property?.number ||
    property?.addresses?.some((a) => a.street || a.zip || a.city || a.district || a.state || a.country);
  const isReviewed = deliveryNote.is_reviewed;
  const isBilled = deliveryNote.is_billed;
  const isCanceled = deliveryNote.is_canceled;

  const handlePdf = async () => {
    setGeneratingPdf(true);
    const result = await generatePdf({ id: deliveryNote.id });
    setGeneratingPdf(false);
    if (result.data) {
      const url = URL.createObjectURL(result.data);
      window.open(url, "_blank");
    }
  };

  const handleCopyLink = async () => {
    const result = await deliveryNotesApi.getShareLink({
      id: deliveryNote.id,
    });
    if (result.data?.url) {
      await navigator.clipboard.writeText(result.data.url);
      addToast("success", t("common.link_copied"));
    }
  };

  // ugly but works simple for now
  return (
    <div>
      <Breadcrumb
        items={[
          { label: t("delivery_notes.delivery_notes"), to: listUrl },
          { label: `${t("common.number_short")} ${deliveryNote.number}` },
        ]}
      >
        {navVisible && (
          <div className="btn-group d-none d-md-flex">
            <button
              type="button"
              className="btn btn-white btn-sm"
              disabled={prevNavId === null && !hasPrevPage}
              onClick={navigatePrev}
            >
              <i className="fa fa-chevron-left" />
            </button>
            <button
              type="button"
              className="btn btn-white btn-sm"
              disabled={nextNavId === null && !hasNextPage}
              onClick={navigateNext}
            >
              <i className="fa fa-chevron-right" />
            </button>
          </div>
        )}
      </Breadcrumb>

      <MobileActionBarContent>
        <div className="d-flex gap-2">
          <div className="btn-group flex-grow-1">
            <button
              type="button"
              className="btn btn-default"
              onClick={() => setShowConfirmCopy(true)}
            >
              <i className="fa fa-copy fa-fw me-1" />
              {t("common.copy")}
            </button>
            <Link
              to={canEdit && !isReviewed ? `/delivery_notes/${deliveryNote.id}/edit` : "#"}
              className={`btn btn-success ${(!canEdit || isReviewed) && "disabled"}`}
            >
              <i className="fa fa-pencil fa-fw me-1" />
              {t("common.edit")}
            </Link>
          </div>
          <div className="btn-group">
            <button
              type="button"
              className="btn btn-default"
              disabled={!prevNavId && !hasPrevPage}
              onClick={navigatePrev}
            >
              <i className="fa fa-chevron-left" />
            </button>
            <button
              type="button"
              className="btn btn-default"
              disabled={!nextNavId && !hasNextPage}
              onClick={navigateNext}
            >
              <i className="fa fa-chevron-right" />
            </button>
          </div>
          <Link to={listUrl} className="btn btn-danger" title={t("common.close")}>
            <i className="fa fa-times" />
          </Link>
        </div>
      </MobileActionBarContent>

      <div className="row">
        <div className="col-12 col-lg-9">
          <div className="delivery_note">
            <div className="delivery_note-company">
              <span className="float-end d-none d-md-inline">
                <div className="btn-group mb-10px me-2">
                  <Link
                    to={canEdit && !isReviewed ? `/delivery_notes/${deliveryNote.id}/edit` : "#"}
                    className={`btn btn-sm btn-white ${(!canEdit || isReviewed) && "disabled"}`}
                  >
                    <i className="fa fa-pencil t-plus-1 fa-fw fa-lg" /> {t("common.edit")}
                  </Link>
                  <button
                    type="button"
                    className="btn btn-sm btn-white"
                    onClick={() => setShowConfirmCopy(true)}
                  >
                    <i className="fa fa-copy t-plus-1 fa-fw fa-lg" /> {t("common.copy")}
                  </button>
                  {isManager && (
                    <>
                      <button type="button" disabled={generatingPdf} className="btn btn-sm btn-white" onClick={handlePdf}>
                        <i className={`fa fa-${generatingPdf ? "spinner fa-spin" : "print"} t-plus-1 fa-fw fa-lg`} />{" "}
                        {t("common.print")}
                      </button>
                      <button type="button" className="btn btn-sm btn-white dropdown-toggle" data-bs-toggle="dropdown">
                        <i className="fa fa-caret-down" />
                      </button>
                      <div className="dropdown-menu dropdown-menu-end">
                        <button type="button" className="dropdown-item" onClick={() => setShowEmailModal(true)}>
                          <i className="fa fa-envelope fa-fw me-1" />
                          {t("common.send_email")}
                        </button>
                        <button type="button" className="dropdown-item" onClick={handleCopyLink}>
                          <i className="fa fa-link fa-fw me-1" />
                          {t("common.copy_link")}
                        </button>
                      </div>
                    </>
                  )}
                </div>
                {canReview && !isReviewed && (
                  <button
                    type="button"
                    onClick={() => setShowConfirmReview(true)}
                    className="btn btn-sm btn-success mb-10px me-2"
                  >
                    <i className="fa fa-question-circle t-plus-1 fa-fw fa-lg" /> {t("delivery_notes.states.reviewed")}
                  </button>
                )}
                {canReview && isReviewed && (
                  <Fragment>
                    <div className="btn-group mb-10px">
                      {!isCanceled && (
                        <div className="form-inline">
                          <input
                            className="form-control form-control-sm"
                            placeholder={t("invoices.number")}
                            disabled={isBilled || isCanceled}
                            onChange={(e) => setInvoiceNumber(e.target.value)}
                            value={invoiceNumber}
                          />
                        </div>
                      )}
                      <button
                        type="button"
                        onClick={() => setShowConfirmBilled(true)}
                        disabled={isBilled || isCanceled || !invoiceNumber}
                        className="btn btn-sm btn-success"
                      >
                        <i className={`fa fa-${isBilled ? "check" : "question-circle"} t-plus-1 fa-fw fa-lg`} />{" "}
                        {isCanceled ? t("delivery_notes.states.canceled") : t("delivery_notes.states.billed")}
                      </button>
                      <button
                        type="button"
                        data-bs-toggle="dropdown"
                        className="btn btn-sm btn-success dropdown-toggle"
                        disabled={isBilled || isCanceled}
                      >
                        <i className="fa fa-caret-down" />
                      </button>
                      <div className="dropdown-menu dropdown-menu-end">
                        <button
                          onClick={() => setShowConfirmCanceled(true)}
                          type="button"
                          className="dropdown-item"
                          disabled={isBilled || isCanceled}
                        >
                          <i className={`fa fa-${isBilled ? "check" : "question-circle"} t-plus-1 fa-fw fa-lg`} />{" "}
                          {t("delivery_notes.states.canceled")}
                        </button>
                      </div>
                    </div>
                  </Fragment>
                )}
              </span>
              <span>{whitelabel.name}</span>
            </div>
            <div className="row m-0 p-2 bg-light">
              {hasCustomer && (
                <div className="col-12 col-md p-0">
                  <small>
                    {t("customers.customer")}
                    {deliveryNote.customer.number && ` (Nr. ${deliveryNote.customer.number})`}
                  </small>
                  <div>{deliveryNote.customer.name}</div>
                  {deliveryNote.customer.addresses.map((address) => (
                    <address key={address.id}>
                      <span className="text-nowrap">{address.street}</span>
                      <br />
                      {!_.isEmpty(address.zip) && <>{address.zip}</>}
                      {!_.isEmpty(address.city) && <> {address.city}</>}
                      {!_.isEmpty(address.district) && (
                        <>
                          <br />
                          {address.district}
                        </>
                      )}
                      {!_.isEmpty(address.state) && (
                        <>
                          <br />
                          {address.state}
                        </>
                      )}
                      {!_.isEmpty(address.country) && (
                        <>
                          <br />
                          {address.country}
                        </>
                      )}
                    </address>
                  ))}
                </div>
              )}
              {hasProperty && (
                <div className="col-12 col-md p-0">
                  <small>
                    {t("customers.properties.property")}
                    {deliveryNote.customer_property.number && ` (Nr. ${deliveryNote.customer_property.number})`}
                  </small>
                  {!_.isEmpty(deliveryNote.customer_property.name?.trim()) && (
                    <div>{deliveryNote.customer_property.name}</div>
                  )}
                  {deliveryNote.customer_property.addresses.map((address) => {
                    const nameMatchesStreet = deliveryNote.customer_property.name?.trim() === address.street?.trim();
                    return (
                      <address key={address.id}>
                        {!!address.street && !nameMatchesStreet && (
                          <>
                            <span className="text-nowrap">{address.street}</span>
                            <br />
                          </>
                        )}
                        {!_.isEmpty(address.zip) && <>{address.zip}</>}
                        {!_.isEmpty(address.city) && <> {address.city}</>}
                        {!_.isEmpty(address.district) && (
                          <>
                            <br />
                            {address.district}
                          </>
                        )}
                        {!_.isEmpty(address.state) && (
                          <>
                            <br />
                            {address.state}
                          </>
                        )}
                        {!_.isEmpty(address.country) && (
                          <>
                            <br />
                            {address.country}
                          </>
                        )}
                      </address>
                    );
                  })}
                </div>
              )}
              <div className="col-12 col-md-auto p-0">
                <small>{t("delivery_notes.delivery_note")}</small>
                <table>
                  <tbody>
                    <tr>
                      <td className="pe-2">{t("delivery_notes.number")}:</td>
                      <td className="text-nowrap">{deliveryNote.number}</td>
                    </tr>
                    <tr>
                      <td className="pe-2">{t("customers.number")}:</td>
                      <td className="text-nowrap">{deliveryNote.customer.number}</td>
                    </tr>
                    <tr>
                      <td className="pe-2">{t("customers.properties.number")}:</td>
                      <td className="text-nowrap">{deliveryNote.customer_property.number}</td>
                    </tr>
                    <tr>
                      <td className="pe-2">{t("delivery_notes.date")}:</td>
                      <td>{formatDate(deliveryNote.delivered_at)}</td>
                    </tr>
                    <tr>
                      <td className="pe-2">{t("common.expert")}:</td>
                      <td className="text-nowrap">
                        {deliveryNote.created_by.name}
                        {deliveryNote.created_by.number && (
                          <span className="text-muted"> ({deliveryNote.created_by.number})</span>
                        )}
                      </td>
                    </tr>
                    {deliveryNote.coworkers?.map((coworker) => (
                      <tr key={coworker.id}>
                        <td className="pe-2">{t("common.expert")}:</td>
                        <td className="text-nowrap">
                          {coworker.name}
                          {coworker.number && <span className="text-muted"> ({coworker.number})</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            <div className="delivery_note-content">
              <div className="mt-4">
                <h6>Artikel</h6>
                <DeliveryNoteDetailsNoteTable deliveryNote={deliveryNote} />
              </div>
              {!_.isEmpty(deliveryNote.documentation) && (
                <div className="mt-4">
                  <h6>Dokumentation</h6>
                  <DeliveryNoteDetailsDocTable documentation={deliveryNote.documentation} />
                </div>
              )}
            </div>
            <div className="">
              {deliveryNote.remark !== "" && (
                <div className="row mb-3">
                  <div className="col">
                    <div className="text-dark">
                      <h5>{t("menu.remark")}</h5>
                      <span
                        dangerouslySetInnerHTML={{
                          __html: deliveryNote.remark,
                        }}
                      />
                    </div>
                  </div>
                </div>
              )}
              {deliveryNote.files.length > 0 && (
                <div className="row mb-3">
                  <div className="col">
                    <div className="float-start">
                      <ImageThumbnailGallery files={deliveryNote.files} />
                    </div>
                  </div>
                </div>
              )}
              <div className="row mb-3">
                {!!deliveryNote.employee_signature && (
                  <div className="col">
                    <div className="text-dark">
                      <h5>
                        {t("customers.menu.signature")} {t("common.expert")}
                      </h5>
                      <img
                        src={deliveryNote.employee_signature}
                        className="mt-2"
                        style={{ maxWidth: "150px" }}
                        alt=""
                      />
                      {!!deliveryNote.employee_signature_name && (
                        <div className="mt-2">
                          <small>{deliveryNote.employee_signature_name}</small>
                        </div>
                      )}
                    </div>
                  </div>
                )}
                {!!deliveryNote.customer_signature && (
                  <div className="col text-dark">
                    <div className="float-end text-dark">
                      <h5>
                        {t("customers.menu.signature")} {t("customers.customer")}
                      </h5>
                      <img
                        src={deliveryNote.customer_signature}
                        className="mt-2"
                        style={{ maxWidth: "150px" }}
                        alt=""
                      />
                      {!!deliveryNote.customer_signature_name && (
                        <div className="mt-2">
                          <small>{deliveryNote.customer_signature_name}</small>
                        </div>
                      )}
                      {!!deliveryNote.customer_signature_date && (
                        <div>
                          <small className="text-muted">{formatDateTime(deliveryNote.customer_signature_date)}</small>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
              {!!whitelabel.pdf_footer_text && (
                <p className="text-center mb-5px fw-bold">{whitelabel.pdf_footer_text}</p>
              )}
              <p className="text-center">
                {!!whitelabel.url && (
                  <span className="me-10px">
                    <i className="fa fa-fw fa-lg fa-globe"></i> {whitelabel.url}
                  </span>
                )}
                {!!whitelabel.phone && (
                  <span className="me-10px">
                    <i className="fa fa-fw fa-lg fa-phone-volume"></i> {whitelabel.phone}
                  </span>
                )}
                {!!whitelabel.fax && (
                  <span className="me-10px">
                    <i className="fa fa-fw fa-lg fa-fax"></i> {whitelabel.fax}
                  </span>
                )}
                {!!whitelabel.email && (
                  <span className="me-10px">
                    <i className="fa fa-fw fa-lg fa-envelope"></i> {whitelabel.email}
                  </span>
                )}
              </p>
            </div>
          </div>
        </div>
        <div className="col-12 col-lg-3 mt-3 mt-lg-0">
          <h5>{t("delivery_notes.assigned_to")}</h5>
          {isManager ? (
            <div className="input-group mb-3">
              <select
                className="form-select"
                disabled={isBilled || isCanceled}
                value={deliveryNote.assigned_to?.id ?? ""}
                onChange={(e) => {
                  const value = e.target.value;
                  assignDeliveryNote({
                    id: deliveryNote.id,
                    assigned_to_id: value ? Number(value) : null,
                  });
                }}
              >
                <option value="">{t("delivery_notes.unassigned")}</option>
                {allUsers
                  .filter((u) => u.active)
                  .map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.name}
                    </option>
                  ))}
              </select>
              <button
                type="button"
                className="btn btn-red"
                data-bs-toggle="tooltip"
                data-bs-placement="top"
                title={t("delivery_notes.assign_to_me")}
                disabled={isBilled || isCanceled || deliveryNote.assigned_to?.id === me.id}
                onClick={() =>
                  assignDeliveryNote({
                    id: deliveryNote.id,
                    assigned_to_id: me.id,
                  })
                }
              >
                <i className="fa fa-user" />
              </button>
            </div>
          ) : (
            <p className="mb-3">
              {deliveryNote.assigned_to ? deliveryNote.assigned_to.name : t("delivery_notes.unassigned")}
            </p>
          )}

          <h5>{t("menu.comments")}</h5>
          {deliveryNote.comments?.map((comment) => {
            const canManage = me.id === comment.created_by?.id || hasRole(Role.ADMINISTRATOR) || hasRole(Role.MANAGER);
            const isEditing = editingCommentId === comment.id;

            return (
              <div key={comment.id} className="mb-2 p-2 bg-light rounded">
                <div className="d-flex justify-content-between align-items-start">
                  <small className="text-muted">
                    {comment.created_by?.name}
                    {comment.created_at && <> &middot; {formatDateTime(comment.created_at)}</>}
                    {comment.updated_at && comment.updated_at !== comment.created_at && (
                      <> &middot; {t("comments.edited")}</>
                    )}
                  </small>
                  {canManage && !isEditing && (
                    <div className="btn-group btn-group-sm">
                      <button
                        type="button"
                        className="btn btn-link btn-sm p-0 me-1"
                        title={t("common.edit")}
                        onClick={() => {
                          setEditingCommentId(comment.id);
                          setEditingCommentText(comment.text);
                        }}
                      >
                        <i className="fa fa-pencil" />
                      </button>
                      <button
                        type="button"
                        className="btn btn-link btn-sm p-0 text-danger"
                        title={t("common.delete")}
                        onClick={() => setShowConfirmDelete(comment.id)}
                      >
                        <i className="fa fa-trash" />
                      </button>
                    </div>
                  )}
                </div>
                {isEditing ? (
                  <div className="mt-1">
                    <textarea
                      className="form-control form-control-sm mb-1"
                      rows={2}
                      value={editingCommentText}
                      onChange={(e) => setEditingCommentText(e.target.value)}
                      disabled={submittingComment}
                    />
                    <div className="btn-group btn-group-sm">
                      <button
                        type="button"
                        className="btn btn-sm btn-primary"
                        disabled={!editingCommentText.trim() || submittingComment}
                        onClick={async () => {
                          setSubmittingComment(true);
                          await updateComment({
                            id: noteId,
                            commentId: comment.id,
                            text: editingCommentText.trim(),
                          });
                          setEditingCommentId(null);
                          setEditingCommentText("");
                          setSubmittingComment(false);
                        }}
                      >
                        {t("common.save")}
                      </button>
                      <button
                        type="button"
                        className="btn btn-sm btn-default"
                        onClick={() => {
                          setEditingCommentId(null);
                          setEditingCommentText("");
                        }}
                      >
                        {t("common.cancel")}
                      </button>
                      <AudioRecordButton
                        disabled={submittingComment}
                        onTranscription={(text) => setEditingCommentText((prev) => (prev ? `${prev} ${text}` : text))}
                      />
                    </div>
                  </div>
                ) : (
                  <div>{comment.text}</div>
                )}
              </div>
            );
          })}
          <div className="mt-3">
            <textarea
              className="form-control form-control-sm mb-2"
              rows={2}
              placeholder={t("comments.add_placeholder")}
              value={commentText}
              onChange={(e) => setCommentText(e.target.value)}
              disabled={submittingComment}
            />
            <div className="btn-group">
              <button
                type="button"
                className="btn btn-sm btn-red"
                disabled={!commentText.trim() || submittingComment}
                onClick={async () => {
                  setSubmittingComment(true);
                  await addComment({ id: noteId, text: commentText.trim() });
                  setCommentText("");
                  setSubmittingComment(false);
                }}
              >
                {t("comments.add")}
              </button>
              <AudioRecordButton
                disabled={submittingComment}
                onTranscription={(text) => setCommentText((prev) => (prev ? `${prev} ${text}` : text))}
              />
            </div>
          </div>
        </div>
      </div>
      {showConfirmReview && (
        <ConfirmDialog
          title={t("delivery_notes.states.reviewed")}
          message={t("delivery_notes.menu.confirm_reviewed")}
          confirmLabel={t("delivery_notes.states.reviewed")}
          onCancel={() => setShowConfirmReview(false)}
          onConfirm={() => {
            setShowConfirmReview(false);
            changeState({
              id: deliveryNote.id,
              state: DELIVERY_NOTE_STATE.REVIEWED,
            });
          }}
        />
      )}
      {showConfirmBilled && (
        <ConfirmDialog
          title={t("delivery_notes.states.billed")}
          message={t("delivery_notes.menu.confirm_billed")}
          confirmLabel={t("delivery_notes.states.billed")}
          onCancel={() => setShowConfirmBilled(false)}
          onConfirm={async () => {
            setShowConfirmBilled(false);
            const result = await changeState({
              id: deliveryNote.id,
              state: DELIVERY_NOTE_STATE.BILLED,
              attrs: {
                [DELIVERY_NOTE_ATTRS.INVOICE_NUMBER]: invoiceNumber,
              },
            });
            if (result.data) {
              setPendingAdvanceUrl(nextNavId ? buildNoteUrl(nextNavId) : "/delivery_notes");
            }
          }}
        />
      )}
      {showConfirmDelete && (
        <ConfirmDialog
          title={t("comments.delete")}
          message={t("comments.confirm_delete")}
          confirmLabel={t("common.delete")}
          confirmClassName="btn btn-danger"
          onCancel={() => setShowConfirmDelete(null)}
          onConfirm={async () => {
            const commentId = showConfirmDelete;
            setShowConfirmDelete(null);
            await deleteComment({ id: noteId, commentId });
          }}
        />
      )}
      {showEmailModal && (
        <DeliveryNoteEmailModal deliveryNote={deliveryNote} onClose={() => setShowEmailModal(false)} />
      )}
      {showConfirmCanceled && (
        <ConfirmDialog
          title={t("delivery_notes.states.canceled")}
          message={t("delivery_notes.menu.confirm_canceled")}
          confirmLabel={t("delivery_notes.states.canceled")}
          confirmClassName="btn btn-danger"
          onCancel={() => setShowConfirmCanceled(false)}
          onConfirm={async () => {
            setShowConfirmCanceled(false);
            const result = await changeState({
              id: deliveryNote.id,
              state: DELIVERY_NOTE_STATE.CANCELED,
            });
            if (result.data) {
              setPendingAdvanceUrl(nextNavId ? buildNoteUrl(nextNavId) : "/delivery_notes");
            }
          }}
        />
      )}
      {showConfirmCopy && (
        <ConfirmDialog
          title={t("common.copy")}
          message={t("delivery_notes.menu.confirm_copy")}
          confirmLabel={t("common.copy")}
          onCancel={() => setShowConfirmCopy(false)}
          onConfirm={() => {
            setShowConfirmCopy(false);
            navigate(
              `/delivery_notes/add?templateId=${deliveryNote.template.id}&copyFromId=${deliveryNote.id}`,
            );
          }}
        />
      )}
      {pendingAdvanceUrl && (
        <CountdownDialog
          message={t("delivery_notes.menu.advancing_next")}
          onComplete={() => {
            navigate(pendingAdvanceUrl);
            removeFromList(noteId);
          }}
          onCancel={() => setPendingAdvanceUrl(null)}
        />
      )}
      {DEV_MODE && (
        <pre
          className="m-t-xl"
          style={{
            fontSize: "1rem",
            padding: ".25rem .5rem",
            overflowX: "scroll",
          }}
        >
          delivery_note: {JSON.stringify(deliveryNote, null, 2)}
        </pre>
      )}
    </div>
  );
}
